import React, { useState, useEffect } from "react";
import { useDispatch } from "react-redux";
import {
  usePreviewGiftcardMutation,
  useConfirmGiftcardMutation,
  useConfirmGiftcardWithOtpMutation,
  useGetGiftcardStatusQuery,
  GiftcardProduct,
  GiftcardConfirmRequest,
} from "../../../features/giftcards/giftcardsSlice";
import { openOtpModal } from "../../../features/withdraw-otp/withdrawOtpSlice";
import { WithdrawOtpModal } from "../withdraw-now/WithdrawOtpModal";
import { toast } from "react-hot-toast";

// Currency symbol helper
const getCurrencySymbol = (code: string): string => {
  const symbols: Record<string, string> = {
    USD: "$", EUR: "€", GBP: "£", NGN: "₦", CAD: "C$", AUD: "A$",
    JPY: "¥", CNY: "¥", INR: "₹", BRL: "R$", MXN: "$", ZAR: "R",
    KRW: "₩", THB: "฿", PHP: "₱", IDR: "Rp", MYR: "RM", SGD: "S$",
    HKD: "HK$", TWD: "NT$", AED: "د.إ", SAR: "﷼", PKR: "₨", BDT: "৳",
    VND: "₫", EGP: "E£", KES: "KSh", GHS: "GH₵", TZS: "TSh", UGX: "USh",
  };
  return symbols[code] || `${code} `;
};

interface PurchaseFormProps {
  product: GiftcardProduct;
  countryCode: string; // Required for cached product lookup
  onComplete: () => void;
  onBack: () => void;
}

export const PurchaseForm: React.FC<PurchaseFormProps> = ({
  product,
  countryCode,
  onComplete,
  onBack,
}) => {
  // For FIXED: store the index of selected amount to correctly map recipient -> sender
  // For RANGE: user enters recipient amount (gift card face value)
  const dispatch = useDispatch();
  const [selectedIndex, setSelectedIndex] = useState<number>(-1);
  const [customRecipientAmount, setCustomRecipientAmount] = useState<number>(0);
  const [quantity, setQuantity] = useState<number>(1);
  const [recipientEmail, setRecipientEmail] = useState("");
  const [pendingPurchasePayload, setPendingPurchasePayload] = useState<GiftcardConfirmRequest | null>(null);

  const { data: status } = useGetGiftcardStatusQuery();
  const [preview, { data: previewData, isLoading: previewLoading, error: previewError }] =
    usePreviewGiftcardMutation();
  const [confirm, { isLoading: confirmLoading }] = useConfirmGiftcardMutation();
  const [confirmWithOtp] = useConfirmGiftcardWithOtpMutation();

  const recipientSymbol = getCurrencySymbol(product.recipientCurrencyCode);
  const senderSymbol = getCurrencySymbol(product.senderCurrencyCode);

  // Get selected amounts based on index (more reliable than map lookup)
  const selectedRecipientAmount = selectedIndex >= 0 ? product.fixedRecipientAmounts[selectedIndex] : 0;
  const selectedSenderAmount = selectedIndex >= 0 ? product.fixedSenderAmounts[selectedIndex] : 0;

  // Convert recipient amount to sender amount for RANGE products
  const convertRecipientToSender = (recipientAmt: number): number => {
    if (product.senderCurrencyCode === product.recipientCurrencyCode) {
      return recipientAmt; // Same currency, no conversion needed
    }
    // Use ratio from min denominations to convert
    const senderMin = product.minSenderAmount || 1;
    const senderMax = product.maxSenderAmount || Infinity;
    const recipientMin = product.minRecipientAmount || 1;
    const recipientMax = product.maxRecipientAmount || Infinity;
    const ratio = senderMin / recipientMin;

    let converted = recipientAmt * ratio;

    // Clamp to valid sender bounds to handle floating point precision at boundaries
    // If user enters exactly min/max recipient, ensure sender is exactly min/max
    if (recipientAmt <= recipientMin) {
      converted = senderMin;
    } else if (recipientAmt >= recipientMax) {
      converted = senderMax;
    }

    // Round to 2 decimal places
    return Math.round(converted * 100) / 100;
  };

  // Check if a valid amount is selected/entered
  const hasValidAmount = (): boolean => {
    if (product.denominationType === "FIXED") {
      return selectedRecipientAmount > 0;
    }
    return customRecipientAmount > 0 && !rangeValidationError;
  };

  // Validate recipient amount for RANGE products (returns error message or null)
  const validateRecipientAmount = (): string | null => {
    if (product.denominationType === "FIXED") return null;
    if (customRecipientAmount <= 0) return null; // Don't show error for empty input

    const min = product.minRecipientAmount || 0;
    const max = product.maxRecipientAmount || Infinity;

    if (customRecipientAmount < min || customRecipientAmount > max) {
      return `Amount must be between ${recipientSymbol}${min.toLocaleString()} and ${recipientSymbol}${max.toLocaleString()} ${product.recipientCurrencyCode}`;
    }
    return null;
  };

  const rangeValidationError = validateRecipientAmount();

  // Debounced preview - only call API if amount is valid
  useEffect(() => {
    // Don't call API if there's a validation error
    if (rangeValidationError) return;

    if (product.denominationType === "FIXED") {
      // FIXED: send recipient amount - backend will look up exact sender amount from mapping
      if (selectedRecipientAmount > 0 && quantity > 0) {
        const timer = setTimeout(() => {
          preview({ productId: product.productId, countryCode, recipientAmount: selectedRecipientAmount, quantity });
        }, 500);
        return () => clearTimeout(timer);
      }
    } else {
      // RANGE: send recipient amount directly (backend handles conversion)
      if (customRecipientAmount > 0 && quantity > 0) {
        const timer = setTimeout(() => {
          preview({ productId: product.productId, countryCode, recipientAmount: customRecipientAmount, quantity });
        }, 500);
        return () => clearTimeout(timer);
      }
    }
  }, [selectedIndex, selectedRecipientAmount, customRecipientAmount, quantity, product.productId, product.denominationType, preview, rangeValidationError, countryCode]);

  const handleQuantityChange = (delta: number) => {
    const maxQty = status?.maxQuantity || 10;
    const newQty = Math.max(1, Math.min(maxQty, quantity + delta));
    setQuantity(newQty);
  };

  const buildConfirmBody = (): GiftcardConfirmRequest => {
    const recipientAmt = product.denominationType === "FIXED"
      ? selectedRecipientAmount
      : customRecipientAmount;
    return {
      productId: product.productId,
      countryCode,
      recipientAmount: recipientAmt,
      quantity,
      recipientEmail: recipientEmail || undefined,
    };
  };

  const handleConfirm = async () => {
    if (!previewData?.canPurchase) {
      toast.error("Insufficient balance");
      return;
    }

    const body = buildConfirmBody();

    try {
      const result = await confirm(body).unwrap();
      toast.success(result.message || "Gift card purchase initiated!");
      onComplete();
    } catch (error: any) {
      if (
        error?.status === 401 &&
        error?.data?.message?.includes("OTP required") &&
        error?.data?.challengeId &&
        error?.data?.instructions
      ) {
        setPendingPurchasePayload(body);
        dispatch(
          openOtpModal({
            challengeId: error.data.challengeId,
            ttlSeconds: error.data.instructions.ttlSeconds || 300,
          })
        );
        toast.error("OTP verification required. Check your email.");
      } else {
        toast.error(error?.data?.message || "Failed to purchase gift card");
      }
    }
  };

  const handleGiftcardOtpSubmit = async (challengeId: string, otpCode: string) => {
    if (!pendingPurchasePayload) {
      throw new Error("No pending purchase found");
    }
    const result = await confirmWithOtp({
      body: pendingPurchasePayload,
      challengeId,
      otpCode,
    }).unwrap();
    setPendingPurchasePayload(null);
    toast.success(result.message || "Gift card purchase initiated!");
    onComplete();
  };

  const formatNGN = (value: number) => {
    return new Intl.NumberFormat("en-NG", {
      style: "currency",
      currency: "NGN",
      minimumFractionDigits: 0,
    }).format(value);
  };

  return (
    <div className="max-w-md mx-auto">
      {/* Product Info */}
      <div className="mb-6">
        <p className="text-white text-lg font-medium">{product.productName}</p>
        <p className="text-gray-400 text-sm">
          {product.denominationType === "FIXED" ? "Select an amount" : "Enter custom amount"}
        </p>
      </div>

      {/* Amount Selection */}
      <div className="mb-6">
        {product.denominationType === "FIXED" ? (
          <>
            <label className="block text-gray-400 text-sm mb-2">
              Select Gift Card Value ({product.recipientCurrencyCode})
            </label>
            <div className="grid grid-cols-3 gap-2">
              {product.fixedRecipientAmounts.map((recipientAmt, index) => (
                <button
                  key={index}
                  onClick={() => setSelectedIndex(index)}
                  className={`px-3 py-3 rounded-lg border text-center transition-colors ${
                    selectedIndex === index
                      ? "bg-primary text-black border-primary"
                      : "bg-gray-800 text-white border-gray-700 hover:border-gray-600"
                  }`}
                >
                  <div className="font-medium">{recipientSymbol}{recipientAmt.toLocaleString()}</div>
                </button>
              ))}
            </div>
          </>
        ) : (
          <>
            <label className="block text-gray-400 text-sm mb-2">
              Gift Card Value ({product.recipientCurrencyCode})
            </label>
            <input
              type="number"
              min={product.minRecipientAmount || 1}
              max={product.maxRecipientAmount || 10000}
              step="0.01"
              value={customRecipientAmount || ""}
              onChange={(e) => setCustomRecipientAmount(Number(e.target.value))}
              placeholder={`${product.minRecipientAmount} - ${product.maxRecipientAmount}`}
              className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white text-lg focus:outline-none focus:border-primary"
            />
            <p className="text-gray-500 text-xs mt-1">
              Min: {recipientSymbol}{product.minRecipientAmount?.toLocaleString()} - Max: {recipientSymbol}{product.maxRecipientAmount?.toLocaleString()}
            </p>
          </>
        )}
        {/* Show frontend validation error first, then backend error */}
        {rangeValidationError && (
          <p className="text-red-500 text-sm mt-2">{rangeValidationError}</p>
        )}
        {!rangeValidationError && previewError && (
          <p className="text-red-500 text-sm mt-2">
            {(previewError as any)?.data?.message || "Invalid amount"}
          </p>
        )}
      </div>

      {/* Quantity Selector */}
      <div className="mb-6">
        <label className="block text-gray-400 text-sm mb-2">Quantity</label>
        <div className="flex items-center gap-4">
          <button
            onClick={() => handleQuantityChange(-1)}
            disabled={quantity <= 1}
            className="w-10 h-10 bg-gray-800 rounded-lg text-white disabled:opacity-50"
          >
            -
          </button>
          <span className="text-white text-lg font-medium w-8 text-center">
            {quantity}
          </span>
          <button
            onClick={() => handleQuantityChange(1)}
            disabled={quantity >= (status?.maxQuantity || 10)}
            className="w-10 h-10 bg-gray-800 rounded-lg text-white disabled:opacity-50"
          >
            +
          </button>
          <span className="text-gray-400 text-sm">
            Max: {status?.maxQuantity || 10}
          </span>
        </div>
      </div>

      {/* Recipient Email (Optional) */}
      <div className="mb-6">
        <label className="block text-gray-400 text-sm mb-2">
          Recipient Email (Optional)
        </label>
        <input
          type="email"
          value={recipientEmail}
          onChange={(e) => setRecipientEmail(e.target.value)}
          placeholder="Leave empty to use your email"
          className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white focus:outline-none focus:border-primary"
        />
      </div>

      {/* Fee Breakdown */}
      {previewData && hasValidAmount() && (
        <div className="bg-gray-800 rounded-lg p-4 mb-6">
          <h4 className="text-white font-medium mb-3">Order Summary</h4>

          <div className="space-y-2 text-sm">
            {/* Gift Card Value - show for both FIXED and RANGE */}
            <div className="flex justify-between">
              <span className="text-gray-400">Gift Card Value</span>
              <span className="text-white">
                {recipientSymbol}
                {product.denominationType === "FIXED"
                  ? selectedRecipientAmount.toLocaleString()
                  : customRecipientAmount.toLocaleString()
                } {product.recipientCurrencyCode}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-400">Amount (NGN)</span>
              <span className="text-white">{formatNGN(previewData.amountNGN)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-400">Service Fee</span>
              <span className="text-white">{formatNGN(previewData.gcbuyingFeeNGN)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-400">Processing Fee</span>
              <span className="text-white">{formatNGN(previewData.reloadlyFeeNGN)}</span>
            </div>
            <div className="border-t border-gray-700 my-2" />
            <div className="flex justify-between text-lg">
              <span className="text-white font-medium">Total</span>
              <span className="text-primary font-bold">
                {formatNGN(previewData.totalNGN)}
              </span>
            </div>
          </div>

          <div className="mt-4 pt-4 border-t border-gray-700">
            <div className="flex justify-between text-sm">
              <span className="text-gray-400">Your Balance</span>
              <span className={previewData.canPurchase ? "text-green-500" : "text-red-500"}>
                {formatNGN(previewData.walletBalance)}
              </span>
            </div>
            {!previewData.canPurchase && (
              <p className="text-red-500 text-xs mt-2">
                Insufficient balance. Please fund your wallet.
              </p>
            )}
          </div>

          {previewData.redeemInstructions && (
            <div className="mt-4 pt-4 border-t border-gray-700">
              <p className="text-gray-400 text-xs">
                <strong>Redeem Instructions:</strong> {previewData.redeemInstructions}
              </p>
            </div>
          )}
        </div>
      )}

      {/* Action Buttons */}
      <div className="flex gap-3">
        <button
          onClick={onBack}
          className="flex-1 px-6 py-3 bg-gray-700 text-white rounded-lg hover:bg-gray-600 transition-colors"
        >
          Back
        </button>
        <button
          onClick={handleConfirm}
          disabled={!previewData?.canPurchase || confirmLoading || previewLoading || !hasValidAmount()}
          className="flex-1 px-6 py-3 bg-primary text-black rounded-lg hover:bg-primary-dark transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-medium"
        >
          {confirmLoading ? "Processing..." : "Buy Now"}
        </button>
      </div>

      <WithdrawOtpModal onOtpSubmit={handleGiftcardOtpSubmit} />
    </div>
  );
};
